import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { differenceInCalendarDays } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { LuClock, LuEllipsis, LuEye, LuFileText, LuMail, LuUserPlus } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { OverdueHousehold, ServiceCadence } from '@ndvr/customer-api';

import { ExclamationTriangleIcon } from '@/components/Icons';
import { Badge } from '@/components/ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TooltipProvider } from '@/components/ui/tooltip';
import { convertBackendTimeToBrowser, formatDateTime, titleCase } from '@/utils';
import { useDockState } from '@/views/dock/dock-state';
import { TextLink } from '@/widgets';
import { NewEventSheet } from 'views/calendar/widgets/NewEventSheet';
import { useCommandCenterHouseholds } from 'views/household/household-queries';
import TaskOverlays from 'views/tasks/TaskOverlays';

import { DashboardCard } from './DashboardCard';
import { useCreateServiceCadenceSnooze, useFindHouseholdsWithoutRecentEvents } from './overdue-households-queries';

const columnHelper = createColumnHelper<OverdueHousehold>();

const cadenceDays = (cadence: ServiceCadence): number => {
    if (cadence === ServiceCadence.MONTHLY) return 30;
    if (cadence === ServiceCadence.QUARTERLY) return 90;
    if (cadence === ServiceCadence.SEMI_ANNUAL) return 180;
    return 365;
};

const getDaysOverdue = (h: OverdueHousehold): number => {
    if (!h.lastEventStartTime) return 0;
    const last = convertBackendTimeToBrowser(h.lastEventStartTime, h.lastEventStartTimeZone);
    const daysSince = differenceInCalendarDays(new Date(), last);
    return Math.max(0, daysSince - cadenceDays(h.serviceCadence));
};

export const OverdueHouseholds = () => {
    const navigate = useNavigate();
    const { mutate: snooze } = useCreateServiceCadenceSnooze();
    const { data: overdueHouseholds = [], isLoading: overdueHouseholdIsLoading } =
        useFindHouseholdsWithoutRecentEvents();
    const { addCreateTaskDockItem } = useDockState();
    const { data: allHouseholds = [], isLoading: ccHouseholdIsLoading } = useCommandCenterHouseholds();

    const sortedHouseholds = useMemo(() => {
        // Only include funded households (see KeyMetrics: funded if household.aum is truthy)
        const fundedGUIDs = new Set(allHouseholds.filter((h) => h.aum).map((h) => h.globalAccountGUID));
        return overdueHouseholds
            .filter((h) => fundedGUIDs.has(h.householdGUID))
            .sort((a, b) => getDaysOverdue(b) - getDaysOverdue(a));
    }, [overdueHouseholds, allHouseholds]);

    // Schedule a new event for the household
    const [isNewEventSheetOpen, setIsNewEventSheetOpen] = useState(false);
    const [defaultHousehold, setDefaultHousehold] = useState<{ value: string; label: string } | undefined>(undefined);
    const handleScheduleMeeting = (household: OverdueHousehold) => {
        setDefaultHousehold({
            value: household.householdGUID,
            label: household.householdName || 'Unnamed household',
        });
        setIsNewEventSheetOpen(true);
    };

    const columns = [
        columnHelper.accessor('householdName', {
            id: 'household',
            header: 'Household',
            cell: (info) => {
                const h = info.row.original;
                return (
                    <TextLink to={`/household/${h.householdGUID}`} className="underline-offset-4 hover:underline">
                        {info.getValue()}
                    </TextLink>
                );
            },
        }),
        columnHelper.accessor('serviceCadence', {
            id: 'cadence',
            header: 'Service Cadence',
            cell: (info) => <span className="text-gray-600">{titleCase(info.getValue())}</span>,
        }),
        columnHelper.accessor('lastEventStartTime', {
            id: 'lastMeeting',
            header: 'Last meeting',
            cell: (info) => {
                const h = info.row.original;
                return (
                    <span className="text-gray-600">
                        {h.lastEventStartTime
                            ? formatDateTime(
                                  convertBackendTimeToBrowser(h.lastEventStartTime, h.lastEventStartTimeZone),
                              )
                            : 'Never'}
                    </span>
                );
            },
        }),
        columnHelper.display({
            id: 'overdue',
            header: 'Overdue',
            cell: (info) => {
                const days = getDaysOverdue(info.row.original);
                return days > 0 ? (
                    <Badge variant="secondary">{`${days} day${days === 1 ? '' : 's'} overdue`}</Badge>
                ) : (
                    <Badge variant="outline">No baseline</Badge>
                );
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: () => <div className="text-right">Action</div>,
            cell: (info) => {
                const household = info.row.original;
                return (
                    <div className="flex justify-end">
                        <DropdownMenu>
                            <DropdownMenuTrigger>
                                <div className="inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-blue-50 hover:text-blue-900">
                                    <LuEllipsis className="h-4 w-4" />
                                    <span className="sr-only h-0">Open menu</span>
                                </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/household/${household.householdGUID}`)}>
                                    <LuEye className="mr-2 h-4 w-4" />
                                    View Household
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => navigate(`/household/${household.householdGUID}/notes`)}
                                >
                                    <LuFileText className="mr-2 h-4 w-4" />
                                    Review Notes
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleScheduleMeeting(household)}>
                                    <LuMail className="mr-2 h-4 w-4" />
                                    Schedule Meeting
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => addCreateTaskDockItem(household.householdGUID)}>
                                    <LuUserPlus className="mr-2 h-4 w-4" />
                                    Add Task
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => snooze(household.householdGUID)}>
                                    <LuClock className="mr-2 h-4 w-4" />
                                    Snooze
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                );
            },
        }),
    ];

    const table = useReactTable({
        data: sortedHouseholds,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <DashboardCard
            title={
                <>
                    <ExclamationTriangleIcon />
                    Overdue Households
                </>
            }
            size="twoThirds"
        >
            {ccHouseholdIsLoading || overdueHouseholdIsLoading ? (
                <div className="text-gray-500">Loading...</div>
            ) : sortedHouseholds.length === 0 ? (
                <div className="text-gray-500">No overdue households</div>
            ) : (
                <TooltipProvider delayDuration={0}>
                    <div className="relative">
                        <div className="max-h-[320px] overflow-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <tr key={headerGroup.id} className="border-b">
                                            {headerGroup.headers.map((header) => (
                                                <th key={header.id} className="p-2 text-left">
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column.columnDef.header,
                                                              header.getContext(),
                                                          )}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {table.getRowModel().rows.map((row) => (
                                        <tr key={row.id} className="border-b last:border-b-0">
                                            {row.getVisibleCells().map((cell) => (
                                                <td key={cell.id} className="p-2">
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="pointer-events-none absolute right-0 bottom-0 left-0 z-10 h-4 bg-gradient-to-t from-white to-transparent"></div>
                    </div>
                </TooltipProvider>
            )}
            <NewEventSheet
                key={defaultHousehold?.value || 'none'}
                open={isNewEventSheetOpen}
                onOpenChange={setIsNewEventSheetOpen}
                defaultHousehold={defaultHousehold}
                disableHouseholdSelection={true}
            />
            <TaskOverlays />
        </DashboardCard>
    );
};
